import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, List, MoreHorizontal, Pencil, Plus, RefreshCw, Sparkles, Trash2 } from "lucide-react";

import {
  APIError,
  deleteServer,
  getDeleteImpact,
  getServer,
  listServers,
  rebuildServer,
  refreshServer,
  type AuthMethod,
  type Server,
  type ServerDetail,
  type ToolDiff,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CopyButton, ErrorState, PageHeader, Spinner } from "@/components/Page";
import { ConnectorCatalog } from "@/components/ConnectorCatalog";
import { IntegrationLogo } from "@/components/IntegrationLogo";
import { humanizeError } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { CONNECTORS } from "@/lib/connectorCatalog";
import { ConnectDialog } from "@/components/connect/ConnectDialog";

// Delegate to the shared humanizer so raw backend codes/status text never
// reach the user.
function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}

// An MCP server has kind "http" (or "" for legacy rows written before the
// field existed); kind "openapi" is a pasted OpenAPI/REST/cURL integration.
// Refresh (re-introspection) is an MCP-only concept.
// isMCPKind gates kind-agnostic MCP actions (e.g. "Refresh tools", which
// re-introspects). http + stdio both expose a live tool set that can be
// refreshed; openapi is spec-driven (refresh re-parses, handled elsewhere).
const isMCPKind = (kind: string) => kind === "http" || kind === "stdio" || kind === "";

// kindLabel is the short type badge shown in the integration list. For stdio it
// distinguishes the package runtime (npm vs PyPI) so the source is obvious.
const kindLabel = (kind: string, runtime?: string): string => {
  if (kind === "stdio") return runtime === "python" ? "PyPI" : "npm";
  if (kind === "openapi") return "API";
  return "MCP"; // "" / "http"
};

const METHOD_LABELS: Record<AuthMethod, string> = {
  none: "No auth",
  bearer: "Bearer token",
  api_key: "API key (custom header)",
  basic: "HTTP Basic",
  headers: "Custom headers",
  oauth2_client_credentials: "OAuth — client credentials (machine)",
  oauth2_authorization_code: "OAuth — sign in (browser)",
};

type DiffView = {
  name: string;
  diff: ToolDiff;
  affected: number;
  redeploying: number;
  redeployInProgress: boolean;
};

export function Servers() {
  const navigate = useNavigate();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reconnect, setReconnect] = useState<Server | null>(null);
  const [editServer, setEditServer] = useState<Server | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [busyServer, setBusyServer] = useState<string | null>(null);
  const [diffView, setDiffView] = useState<DiffView | null>(null);
  const [detailName, setDetailName] = useState<string | null>(null);
  const [tab, setTab] = useState<"servers" | "catalog">("servers");
  const toast = useToast();
  const confirm = useConfirm();

  // silent skips the loading skeleton + error banner — used by the background
  // build poll so a still-"installing" package doesn't flash the whole table
  // every 3s. setServers diffs in place (rows are keyed by name), so only a
  // changed row re-renders.
  const load = useCallback((opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) {
      setLoading(true);
      setLoadErr(null);
    }
    return listServers()
      .then(setServers)
      .catch((e) => {
        if (!silent) setLoadErr(errMsg(e, "Couldn't load integrations"));
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  // While any stdio/npx package is still building in the background, poll until
  // every build settles (connected or failed) so rows flip without a manual
  // refresh. The effect re-runs only when the "any building" flag flips, so a
  // single interval runs across polls and is torn down once all builds settle.
  const anyBuilding = servers.some((s) => s.building);
  useEffect(() => {
    if (!anyBuilding) return;
    const id = window.setInterval(() => void load({ silent: true }), 3000);
    return () => window.clearInterval(id);
  }, [anyBuilding, load]);

  // Surface the OAuth "Connect" round-trip result (the control plane
  // redirects back here with ?connected=1 or ?connect_error=…), then clean
  // the URL so a refresh doesn't re-toast.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("connected") === "1") {
      toast.success("Integration connected");
    } else if (q.get("connect_error")) {
      toast.error("We couldn't connect that integration. Double-check its URL and credentials, then try again.");
    } else {
      return;
    }
    window.history.replaceState({}, "", window.location.pathname);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link from the playbook author: ?connect=<catalogId> opens the
  // prefilled connect PAGE for that service.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("connect");
    if (!id) return;
    if (CONNECTORS.find((x) => x.id === id)) {
      navigate(`/servers/new?connect=${encodeURIComponent(id)}`, { replace: true });
    } else {
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefresh = useCallback(
    async (s: Server) => {
      setRefreshing(s.name);
      try {
        const res = await refreshServer(s.name);
        if (res.kind === "reauth") {
          toast.success("Re-authorization required — redirecting to sign in…");
          // This server is connected, so the gated reconnect button won't show;
          // hand off to the consent screen directly, like the connect flow.
          window.location.href = res.authorize_url;
          return;
        }
        if (res.kind === "building") {
          // stdio/npm: the package is rebuilding in the background. The row flips
          // to "installing" and the building-poll picks up the result.
          toast.success("Refreshing tools — rebuilding the package…");
          void load();
          return;
        }
        const redeployInProgress = res.redeploy_in_progress === true;
        toast.success(
          res.redeploying > 0
            ? `Tools refreshed — redeploying ${res.redeploying} project${res.redeploying === 1 ? "" : "s"}`
            : redeployInProgress
              ? "Tools refreshed — another redeploy is already in progress"
              : "Tools refreshed",
        );
        setDiffView({
          name: s.name,
          diff: res.diff,
          affected: res.affected_projects,
          redeploying: res.redeploying,
          redeployInProgress,
        });
        void load();
      } catch (e) {
        toast.error(errMsg(e, "Couldn't refresh tools"));
      } finally {
        setRefreshing(null);
      }
    },
    [load, toast],
  );

  const onRebuild = useCallback(
    async (s: Server) => {
      setBusyServer(s.name);
      try {
        await rebuildServer(s.name);
        toast.success("Rebuilding the package…");
        void load();
      } catch (e) {
        toast.error(errMsg(e, "Couldn't rebuild the package"));
      } finally {
        setBusyServer(null);
      }
    },
    [load, toast],
  );

  const onDelete = useCallback(
    async (s: Server) => {
      setBusyServer(s.name);
      let impact;
      try {
        impact = await getDeleteImpact(s.name);
      } catch (e) {
        setBusyServer(null);
        toast.error(errMsg(e, "Couldn't check delete impact"));
        return;
      }
      setBusyServer(null);

      // Pre-check: a non-empty `emptied` means the API would block the delete
      // (those projects would be left with no tools), so we inform and never
      // call DELETE. The 409 path below is only a race safety net.
      if (impact.emptied.length > 0) {
        await confirm({
          title: `Can't delete ${s.name}`,
          description: `These project${impact.emptied.length === 1 ? "" : "s"} would be left with no tools — edit or remove ${impact.emptied.length === 1 ? "it" : "them"} first: ${impact.emptied.join(", ")}.`,
          confirmText: "Close",
          cancelText: "Cancel",
        });
        return;
      }

      const n = impact.affected.length;
      const ok = await confirm({
        title: `Delete ${s.name}?`,
        description: `This removes the integration and its credentials. ${n} project${n === 1 ? "" : "s"} that use it will be automatically redeployed (same URL + token). This can't be undone.`,
        confirmText: "Delete integration",
        destructive: true,
      });
      if (!ok) return;

      setBusyServer(s.name);
      try {
        const res = await deleteServer(s.name);
        toast.success(
          res.redeploying > 0
            ? `Integration deleted — redeploying ${res.redeploying} project${res.redeploying === 1 ? "" : "s"}`
            : "Integration deleted",
        );
        if (res.redeploy_in_progress) {
          toast.success(
            "Another redeploy is in progress; affected projects will stay flagged until it's re-run.",
          );
        }
        void load();
      } catch (e) {
        if (e instanceof APIError && e.status === 409) {
          toast.error("Can't delete: it's still in use by projects that would be left empty.");
        } else {
          toast.error(errMsg(e, "Couldn't delete integration"));
        }
      } finally {
        setBusyServer(null);
      }
    },
    [confirm, load, toast],
  );

  const editDialogOpen = reconnect !== null || editServer !== null;

  return (
    <div>
      <PageHeader
        title="Integrations"
        description="Connect the apps and tools your agents use. Credentials are stored encrypted and never reach the sandbox."
        action={
          <Button onClick={() => navigate("/servers/new")}>
            <Plus /> Connect integration
          </Button>
        }
      />

      {/* Reconnect / edit of an EXISTING integration stays a dialog (an action
          on a row, not a fresh create). New connects use the /servers/new page. */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(o) => {
          if (!o) {
            setReconnect(null);
            setEditServer(null);
          }
        }}
      >
        {editDialogOpen && (
          <ConnectDialog
            reconnect={reconnect}
            prefill={null}
            edit={editServer}
            onConnected={(_name, building) => {
              setReconnect(null);
              setEditServer(null);
              setTab("servers");
              toast.success(building ? "Installing… building the package" : editServer ? "Integration updated" : "Integration connected");
              void load();
            }}
            onError={(m) => toast.error(m)}
          />
        )}
      </Dialog>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "servers" | "catalog")} className="mt-2">
        <TabsList>
          <TabsTrigger value="servers">Your integrations</TabsTrigger>
          <TabsTrigger value="catalog">Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="servers">
          {loadErr ? (
            <ErrorState message={loadErr} onRetry={load} />
          ) : (
            <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Auth</TableHead>
              <TableHead>Tools</TableHead>
              <TableHead className="w-48" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : servers.length === 0 ? (
              <TableEmpty colSpan={6}>No integrations connected yet — connect one to generate typed wrappers.</TableEmpty>
            ) : (
              servers.map((s) => {
                // stdio integrations have no URL — show the npm package coords as
                // the target instead.
                const url =
                  s.kind === "stdio"
                    ? s.package
                      ? `${s.package}${s.version ? `@${s.version}` : ""}`
                      : ""
                    : s.upstream_url || s.base_url || "";
                return (
                <TableRow key={s.name}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      <IntegrationLogo name={s.name} className="h-5 w-5" />
                      <span className="truncate">{s.name}</span>
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{kindLabel(s.kind, s.runtime)}</Badge>
                  </TableCell>
                  <TableCell className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                    {url ? (
                      <>
                        {url} <CopyButton value={url} />
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{METHOD_LABELS[(s.auth_method || "none") as AuthMethod] ?? s.auth_method}</Badge>
                  </TableCell>
                  <TableCell>
                    {s.connected ? (
                      <button
                        type="button"
                        onClick={() => setDetailName(s.name)}
                        title="View integration details"
                        className="cursor-pointer"
                      >
                        <Badge variant="secondary" className="hover:bg-secondary/70">
                          {s.tools.length}
                        </Badge>
                      </button>
                    ) : s.building ? (
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        <Spinner className="h-3 w-3" /> installing…
                      </Badge>
                    ) : s.build_error ? (
                      <Badge
                        variant="outline"
                        className="border-destructive/40 bg-destructive/10 text-destructive"
                        title={s.build_error}
                      >
                        failed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning">
                        awaiting sign-in
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Integration actions"
                          disabled={refreshing === s.name || busyServer === s.name}
                        >
                          {refreshing === s.name || busyServer === s.name ? <Spinner /> : <MoreHorizontal className="h-4 w-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setDetailName(s.name)}>
                          <List /> Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setEditServer(s)}>
                          <Pencil /> Edit
                        </DropdownMenuItem>
                        {s.connected && isMCPKind(s.kind) && (
                          <DropdownMenuItem onSelect={() => void onRefresh(s)}>
                            <RefreshCw /> Refresh tools
                          </DropdownMenuItem>
                        )}
                        {!s.connected && s.auth_method === "oauth2_authorization_code" && (
                          <DropdownMenuItem onSelect={() => setReconnect(s)}>
                            <Sparkles /> Connect
                          </DropdownMenuItem>
                        )}
                        {s.kind === "stdio" && !s.connected && !s.building && (
                          <DropdownMenuItem onSelect={() => void onRebuild(s)}>
                            <RefreshCw /> Retry build
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => void onDelete(s)}
                        >
                          <Trash2 /> Delete integration
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                );
              })
            )}
            </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="catalog">
          <p className="mb-4 text-sm text-muted-foreground">
            Pick a popular service — we prefill the rest and you sign in. Anything else connects by URL with “Connect
            server” above.
          </p>
          <ConnectorCatalog onPick={(c) => navigate(`/servers/new?connect=${encodeURIComponent(c.id)}`)} />
        </TabsContent>
      </Tabs>

      <ServerDetailDialog name={detailName} onClose={() => setDetailName(null)} />

      <Dialog open={diffView !== null} onOpenChange={(o) => !o && setDiffView(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tools refreshed — {diffView?.name}</DialogTitle>
          </DialogHeader>
          {diffView && (
            <DiffBody
              diff={diffView.diff}
              affected={diffView.affected}
              redeploying={diffView.redeploying}
              redeployInProgress={diffView.redeployInProgress}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiffSection({ label, items, className }: { label: string; items: string[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className={cn("text-xs font-semibold", className)}>{label}</div>
      <ul className="space-y-0.5">
        {items.map((t) => (
          <li key={t} className="font-mono text-xs text-muted-foreground">
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DiffBody({
  diff,
  affected,
  redeploying,
  redeployInProgress,
}: {
  diff: ToolDiff;
  affected: number;
  redeploying: number;
  redeployInProgress: boolean;
}) {
  const empty = diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0;
  return (
    <div className="space-y-3">
      {empty ? (
        <p className="text-sm text-muted-foreground">No changes — tools are already up to date.</p>
      ) : (
        <>
          <DiffSection label="Added" items={diff.added} className="text-success" />
          <DiffSection label="Removed" items={diff.removed} className="text-destructive" />
          <DiffSection label="Changed" items={diff.changed} className="text-warning" />
        </>
      )}
      {redeploying > 0 ? (
        <p className="text-xs text-muted-foreground">
          Redeploying {redeploying} project{redeploying === 1 ? "" : "s"} that use this integration.
        </p>
      ) : redeployInProgress ? (
        <p className="text-xs text-warning">
          Another redeploy is already in progress; affected projects stay flagged until it's re-run.
        </p>
      ) : (
        affected > 0 && (
          <p className="text-xs text-warning">
            {affected} project{affected === 1 ? "" : "s"} {affected === 1 ? "is" : "are"} running the previous tool set — update {affected === 1 ? "it" : "them"} from the Projects tab.
          </p>
        )
      )}
    </div>
  );
}

// ServerDetailDialog fetches one server's full record on open (the list
// endpoint carries tool names only, so descriptions + schemas need a
// dedicated GET /v1/servers/{name}).
function ServerDetailDialog({ name, onClose }: { name: string | null; onClose: () => void }) {
  const [detail, setDetail] = useState<ServerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (name === null) {
      setDetail(null);
      setErr(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getServer(name)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setErr(errMsg(e, "Couldn't load integration details")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [name]);

  return (
    <Dialog open={name !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Integration details</DialogTitle>
          <DialogDescription>{name}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : err ? (
          <p className="text-sm text-destructive">{err}</p>
        ) : detail ? (
          <ServerDetailBody detail={detail} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ServerDetailBody({ detail }: { detail: ServerDetail }) {
  const target =
    detail.kind === "stdio"
      ? detail.package
        ? `${detail.package}${detail.version ? `@${detail.version}` : ""}`
        : "—"
      : detail.upstream_url || detail.base_url || "—";
  const targetLabel = detail.kind === "stdio" ? "Package" : detail.kind === "openapi" ? "Base URL" : "URL";
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-muted-foreground">Type</dt>
        <dd>{kindLabel(detail.kind, detail.runtime)}</dd>
        <dt className="text-muted-foreground">{targetLabel}</dt>
        <dd className="break-all font-mono text-xs">{target}</dd>
        {detail.kind === "stdio" && detail.pinned_version && (
          <>
            <dt className="text-muted-foreground">Resolved</dt>
            <dd className="font-mono text-xs">{detail.pinned_version}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Auth</dt>
        <dd>{METHOD_LABELS[(detail.auth_method || "none") as AuthMethod] ?? detail.auth_method}</dd>
      </dl>
      {detail.build_error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Build failed: {detail.build_error}
        </p>
      )}
      {detail.connected && detail.update_error && (
        <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          Last update didn't land — still serving the previous build
          {detail.pinned_version ? ` (${detail.pinned_version})` : ""}. {detail.update_error}
        </p>
      )}
      <div>
        <h4 className="mb-2 text-sm font-medium">Tools ({detail.tools.length})</h4>
        <ServerToolList detail={detail} />
      </div>
    </div>
  );
}

function ServerToolList({ detail }: { detail: ServerDetail }) {
  if (detail.tools.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No tools yet{!detail.connected ? " — connect the integration to introspect them." : "."}
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {detail.tools.map((t) => (
        <li key={t.name}>
          <details className="group rounded-md border">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 font-mono text-sm [&::-webkit-details-marker]:hidden">
              <span className="truncate">{t.name}</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className="space-y-2 border-t px-3 py-2">
              {t.description ? (
                <p className="text-xs text-muted-foreground">{t.description}</p>
              ) : (
                <p className="text-xs italic text-muted-foreground">No description provided.</p>
              )}
              {t.input_schema != null && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Input schema</summary>
                  <pre className="mt-1 max-h-60 overflow-auto rounded bg-muted/40 p-2 text-[11px] leading-relaxed">
                    {JSON.stringify(t.input_schema, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </details>
        </li>
      ))}
    </ul>
  );
}


export { ConnectDialog };
